import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

export type NavigationEntityType = 'ScheduledMeeting' | 'Note' | 'PhoneCall' | 'SurveyResponse' | 'Contact';

/**
 * Hook for immediate navigation actions using the airesolver route
 * Used by AI assistant components for navigating to entities
 */
export function useAINavigationResolver() {
	const history = useHistory();

	const navigateToEntity = useCallback(
		(
			entityType: NavigationEntityType,
			entityId: string,
			parentEntityType?: NavigationEntityType,
			parentEntityId?: string
		) => {
			const path = getNavigationPath(entityType, entityId, parentEntityType, parentEntityId);
			history.push(path);
		},
		[history]
	);

	return { navigateToEntity };
}

/**
 * Generates the airesolver path for an entity
 * This is a pure utility function for AI assistant navigation
 */
export function getNavigationPath(
	entityType: NavigationEntityType,
	entityId: string,
	parentEntityType?: NavigationEntityType,
	parentEntityId?: string
): string {
	if (parentEntityType && parentEntityId) {
		return `/airesolver/${entityType}/${entityId}/${parentEntityType}/${parentEntityId}`;
	}
	return `/airesolver/${entityType}/${entityId}`;
}

/**
 * Gets the display name for a navigation entity type
 * Used by AI assistant for showing navigation buttons
 */
export function getNavigationDisplayName(entityType: NavigationEntityType): string {
	switch (entityType) {
		case 'ScheduledMeeting':
			return 'View Meeting';
		case 'Note':
			return 'View Note';
		case 'PhoneCall':
			return 'View Phone Call';
		case 'SurveyResponse':
			return 'View Survey Response';
		case 'Contact':
			return 'View Contact';
		default:
			return 'View Item';
	}
}
